import querystring from 'query-string';

import config from '../config';
import { getUrlInfo, getServiceVersion } from '../util/helpers';
import { addEntities, removeEntities } from './entities';
import schemas from '../util/schemas';

export const UPDATE_STREAM = 'UPDATE_STREAM';
export const REMOVE_STREAM = 'REMOVE_STREAM';

export interface UpdateStreamAction {
  type: typeof UPDATE_STREAM;
  name?: string;
  status?: number;
  step?: number | null;
  ws?: StreamWebSocket;
  json?: any;
  increment?: boolean;
}

export interface RemoveStreamAction {
  type: typeof REMOVE_STREAM;
  name: string;
}

export type StreamAction = UpdateStreamAction | RemoveStreamAction;

let lostTimer = 1000 * 60;
let retryTimer = 1000 * 5;

export function setStreamLostTime(timeout: number) {
  lostTimer = timeout;
}

export function setStreamRetryTime(timeout: number) {
  retryTimer = timeout;
}

interface StreamTimers {
  retryTimeout?: ReturnType<typeof setTimeout>;
  lostTimeout?: ReturnType<typeof setTimeout>;
}

interface StreamWebSocket extends WebSocket {
  silent?: boolean;
  stop?: boolean;
}

const timeouts: Record<string, StreamTimers> = {};
const websockets: Record<string, StreamWebSocket> = {};

export const streamStatus = {
  CONNECTING: 1,
  OPEN: 2,
  CLOSED: 3,
  RECONNECTING: 4,
  ERROR: 5,
  LOST: 6,
} as const;

export const steps = {
  CONNECTING: {
    GET_STREAM: 1,
    CREATE_STREAM: 2,
    UPDATE_STREAM: 3,
    OPENING_SOCKET: 4,
    WAITING: 5,
  },
} as const;

export interface StreamJSON {
  name: string;
  meta?: { id?: string; [key: string]: any };
  [key: string]: any;
}

export interface StreamOptions {
  service?: string;
  endPoint?: string;
  version?: string;
  [key: string]: any;
}

export function updateStream(
  name?: string,
  status?: number,
  step?: number | null,
  ws?: StreamWebSocket | null,
  json?: any,
  increment?: boolean,
): UpdateStreamAction {
  const obj: UpdateStreamAction = { type: UPDATE_STREAM };
  if (ws != undefined && name) {
    websockets[name] = ws;
    obj.ws = ws;
  }
  if (name !== undefined) {
    obj.name = name;
  }
  if (status !== undefined) {
    obj.status = status;
  }
  if (step !== undefined) {
    obj.step = step;
  }
  if (json !== undefined) {
    obj.json = json;
  }
  if (increment !== undefined) {
    obj.increment = increment;
  }
  return obj;
}

function clearStreamTimeouts(stream: { name?: string }) {
  if (stream && stream.name && timeouts[stream.name]) {
    if (timeouts[stream.name].retryTimeout) clearTimeout(timeouts[stream.name].retryTimeout);
    if (timeouts[stream.name].lostTimeout) clearTimeout(timeouts[stream.name].lostTimeout);
    delete timeouts[stream.name];
  }
}

function removeStream(name: string): RemoveStreamAction {
  return {
    type: REMOVE_STREAM,
    name,
  };
}

export function closeStream(name: string, silent = false) {
  return (dispatch: any) => {
    clearStreamTimeouts({ name });
    if (websockets[name]) {
      websockets[name].silent = silent;
      websockets[name].stop = true;
      websockets[name].close();
    }
    dispatch(removeStream(name));
  };
}

function getUrl(options: StreamOptions = {}, isEndPoint = false): string {
  const service = (isEndPoint ? options.endPoint : options.service) || 'stream';
  const version = Object.prototype.hasOwnProperty.call(options, 'version')
    ? options.version
    : getServiceVersion(service);
  return `${config.baseUrl + (version ? `/${version}` : '')}/${service}`;
}

function addChildren(message: any, state: any) {
  const dataType = message.meta_object.type;
  const data = message[dataType] || message.data;
  const st = schemas.getSchemaTree(dataType);
  if (data && st.dependencies) {
    const cachedData =
      state.entities[dataType] && state.entities[dataType][data.meta.id];
    st.dependencies.forEach(({ key, type }) => {
      if (!Object.prototype.hasOwnProperty.call(data, key)) {
        if (cachedData) {
          data[key] = cachedData[key];
        } else if (type === 'many') {
          data[key] = [];
        } else {
          data[key] = undefined;
        }
      }
    });
  }
}

function startStream(
  stream: StreamJSON,
  session: string,
  getState: any,
  dispatch: any,
  options: StreamOptions,
  reconnecting?: boolean,
): StreamWebSocket {
  let url = getUrl(options, true);
  if (stream.meta && stream.meta.id) {
    url += `/${stream.meta.id}?x-session=${session}`;
  } else {
    const streamClone: any = { ...stream };
    delete streamClone.name;
    url += `/open?x-session=${session}&${querystring.stringify(streamClone)}`;
  }
  if (window && window.location && window.location.origin && !url.startsWith('http')) {
    url = window.location.origin + url;
  }
  url = url.replace('http', 'ws');
  const ws = new WebSocket(url) as StreamWebSocket;

  websockets[stream.name] = ws;

  dispatch(
    updateStream(
      stream.name,
      reconnecting ? streamStatus.RECONNECTING : streamStatus.CONNECTING,
      steps.CONNECTING.OPENING_SOCKET,
      ws,
      stream,
    ),
  );

  ws.onopen = () => {
    clearStreamTimeouts(stream);
    dispatch(updateStream(stream.name, streamStatus.OPEN, null, ws, stream));
  };

  ws.onmessage = (e: MessageEvent) => {
    try {
      let data: any = JSON.parse(e.data);
      if (data.constructor !== Array) {
        data = [data];
      }
      data.forEach((message: any) => {
        if (!message || !message.meta_object) {
          return;
        }

        const state = getState();
        let parent: any;
        switch (message.event) {
          case 'create':
            addChildren(message, state);
            if (message.meta_object.type === 'state') {
              const st = schemas.getSchemaTree('state');
              if (
                state.entities[st.name] &&
                Object.prototype.hasOwnProperty.call(
                  state.entities[st.name],
                  message.meta_object.id,
                )
              ) {
                dispatch(
                  addEntities(
                    message.meta_object.type,
                    message[message.meta_object.type] || message.data,
                    { reset: false },
                  ),
                );
              } else {
                parent = getUrlInfo(message.path, 1).parent;
                dispatch(
                  addEntities(
                    message.meta_object.type,
                    [message[message.meta_object.type] || message.data],
                    { reset: false, parent: parent === '' ? undefined : parent },
                  ),
                );
              }
            } else {
              parent = getUrlInfo(message.path, 1).parent;
              dispatch(
                addEntities(
                  message.meta_object.type,
                  message[message.meta_object.type] || message.data,
                  { reset: false, parent: parent === '' ? undefined : parent },
                ),
              );
            }
            break;
          case 'update':
            addChildren(message, state);
            dispatch(
              addEntities(
                message.meta_object.type,
                message[message.meta_object.type] || message.data,
                { reset: false },
              ),
            );
            break;
          case 'delete':
            parent = getUrlInfo(message.path, 1).parent;
            dispatch(
              removeEntities(message.meta_object.type, [message.meta_object.id], {
                parent: parent === '' ? undefined : parent,
              }),
            );
            break;
          default:
            break;
        }
      });
    } catch (error) {
      // ignore
    }
  };

  ws.onclose = (e: CloseEvent) => {
    if (!ws.stop && e.code !== 4001) {
      timeouts[stream.name] = {};
      const retryTimeout = setTimeout(() => {
        startStream(stream, session, getState, dispatch, options, true);
      }, retryTimer);
      timeouts[stream.name].retryTimeout = retryTimeout;

      const lostTimeout = setTimeout(() => {
        clearStreamTimeouts(stream);
        if (!ws.silent) {
          dispatch(updateStream(stream.name, streamStatus.LOST, null, null, stream));
        }
        if (websockets[stream.name]) {
          websockets[stream.name].stop = true;
          websockets[stream.name].silent = true;
          websockets[stream.name].close();
        }
      }, lostTimer);
      timeouts[stream.name].lostTimeout = lostTimeout;

      if (!ws.silent) {
        dispatch(
          updateStream(
            stream.name,
            streamStatus.RECONNECTING,
            steps.CONNECTING.WAITING,
            ws,
            stream,
            true,
          ),
        );
      }
    } else if (!ws.silent) {
      dispatch(updateStream(stream.name, streamStatus.CLOSED, e.code, ws, stream));
    }
  };

  return ws;
}

export function openStream(
  streamJSON: StreamJSON = { name: '' },
  session: string | undefined = undefined,
  options: StreamOptions = {},
) {
  return async (dispatch: any, getState: any) => {
    if (!streamJSON || !streamJSON.name) {
      throw new TypeError('openStream requires a name to work');
    }
    let newSession: string | undefined = session;
    if (!newSession) {
      newSession = getState().session && getState().session.meta.id;
    }
    return startStream(streamJSON, newSession as string, getState, dispatch, options);
  };
}
